import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './pages/Login/Login';
import ItemsList from './pages/Items/ItemsList';
import CreateItem from './pages/Items/CreateItem';
import ItemDetails from './pages/Items/ItemDetails';
import Layout from './components/Layout/Layout';
import { useAuth } from './context/AuthContext';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import { defaultErrorHandler } from './services/ErrorHandler';
import { captureException } from './services/sentry';
import { ErrorType } from './models/ApiError';

// Configure React Query with Sentry error logging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        // Convert to ApiError and send to Sentry
        const apiError = defaultErrorHandler.handle(error);
        if (apiError.type !== ErrorType.VALIDATION_ERROR) {
          captureException(error instanceof Error ? error : new Error(String(error)), {
            tags: {
              source: 'react-query',
              mutation: 'true',
            },
          });
        }
      },
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isLoggedIn } = useAuth();

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/" element={<Layout />}>
          {!isLoggedIn ? (
            <>
              <Route index element={<Login />} />
              <Route path="login" element={<Login />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <>
              <Route index element={<ItemsList />} />
              <Route path="items" element={<ItemsList />} />
              <Route path="items/:id" element={<ItemDetails />} />
              <Route path="items/create" element={<CreateItem />} />
              <Route path="items/edit/:id" element={<CreateItem />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Route>
      </Routes>
    </QueryClientProvider>
  );
};

const App: React.FC = () => (
  <Router>
    <SentryErrorBoundary>
      <AppRoutes />
    </SentryErrorBoundary>
  </Router>
);

export default App;
